import { useEffect } from "react";
import { saveProject, type StoredProject } from "../model/storage.ts";

// Short enough that closing right after an edit can't lose it in practice,
// long enough not to write on every keystroke while still typing.
const AUTOSAVE_DEBOUNCE_MS = 800;

/** Debounced, and only once a project is loaded — the very first save (right after opening a new file) happens immediately in `openNewFile` instead, so a project exists in storage before the debounce would otherwise get around to it. */
export function useAutosave(
  loaded: StoredProject | null,
  refreshSavedProjects: () => void,
  reportStorageError: (err: unknown) => void
) {
  useEffect(() => {
    if (!loaded) return;

    let flushed = false;
    const flush = () => {
      if (flushed) return;
      flushed = true;
      void saveProject(loaded).then(refreshSavedProjects).catch(reportStorageError);
    };
    const timer = window.setTimeout(flush, AUTOSAVE_DEBOUNCE_MS);

    // An edit made within the debounce window, right before the tab closes,
    // would otherwise be lost — `pagehide` (tab close/navigate away) and
    // `visibilitychange` going "hidden" (tab switch/minimize, often the
    // ONLY signal a mobile browser gives before it suspends or kills the
    // page outright) both flush the pending save immediately instead of
    // waiting out the rest of the timer.
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);
}
